// Register.jsx - Redesigned with three period cards (Super Earlybird, Earlybird, Standard)
// CORRECTED: Real Razorpay checkout integration (create-order -> checkout.js -> verify -> create registration)

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import {
  RiUserLine,
  RiMailLine,
  RiPhoneLine,
  RiBuildingLine,
  RiBriefcaseLine,
  RiArrowRightLine,
  RiCheckLine,
  RiTicketLine,
  RiHotelLine,
  RiHome2Line,
  RiAddLine,
  RiSubtractLine,
  RiCalendarEventLine,
  RiUserAddLine,
} from "react-icons/ri";
import toast, { Toaster } from "react-hot-toast";
import axios from "axios";
import { API_BASE } from "../config";

axios.defaults.baseURL = API_BASE;

/* ─── RAZORPAY SCRIPT LOADER ───────────────────────────────────────── */
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

/* ─── FONT IMPORT & THEME ─────────────────────────────────────────── */
const FontImport = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');

    *, *::before, *::after {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }

    html {
      scroll-behavior: smooth;
    }

    body {
      background: #F4F9F8;
    }

    ::-webkit-scrollbar {
      width: 6px;
    }

    ::-webkit-scrollbar-track {
      background: #E8F5F3;
    }

    ::-webkit-scrollbar-thumb {
      background: #2A9D8F;
      border-radius: 4px;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    /* Responsive Styles */
    @media(max-width:768px) {
      .t-form-grid {
        grid-template-columns: 1fr !important;
      }

      .t-period-grid {
        grid-template-columns: 1fr !important;
      }

      .t-accom-grid {
        grid-template-columns: 1fr !important;
      }

      .t-confirm-grid {
        grid-template-columns: 1fr !important;
      }

      .t-steps-wrap {
        gap: 2px !important;
      }

      .t-step-connector {
        width: 20px !important;
      }

      .t-card-padding {
        padding: 24px 20px !important;
      }

      .t-hero-padding {
        padding:
          clamp(32px,5vw,48px)
          clamp(16px,3vw,32px)
          clamp(24px,4vw,40px) !important;
      }

      .t-hero-title {
        font-size: clamp(1.6rem,4vw,2.4rem) !important;
      }

      .t-stats-wrap {
        gap: 8px !important;
      }

      .t-stat-item {
        padding: 12px 16px !important;
        min-width: 80px !important;
      }

      .t-stat-number {
        font-size: 22px !important;
      }

      .t-register-wrap {
        padding: 20px !important;
      }

      .t-nav-buttons {
        flex-direction: column-reverse !important;
        gap: 12px !important;
      }

      .t-nav-buttons button {
        width: 100% !important;
        justify-content: center !important;
      }
    }

    @media(max-width:480px) {
      .t-card-padding {
        padding: 16px !important;
      }

      .t-hero-title {
        font-size: 1.4rem !important;
      }

      .t-stat-number {
        font-size: 18px !important;
      }

      .t-stat-label {
        font-size: 10px !important;
      }

      .t-step-number {
        width: 32px !important;
        height: 32px !important;
        font-size: 12px !important;
      }

      .t-step-connector {
        width: 16px !important;
      }

      .t-period-card {
        padding: 20px 16px !important;
      }

      .t-period-price {
        font-size: 26px !important;
      }

      .t-accom-card {
        padding: 16px !important;
      }

      .t-confirm-section {
        padding: 16px !important;
      }

      .t-order-summary {
        padding: 20px !important;
      }

      .t-total-amount {
        font-size: 24px !important;
      }

      .t-success-icon {
        width: 60px !important;
        height: 60px !important;
      }

      .t-success-title {
        font-size: 24px !important;
      }

      .t-btn-mobile {
        width: 100% !important;
        justify-content: center !important;
      }
    }

    @media(max-width:360px) {
      .t-hero-title {
        font-size: 1.2rem !important;
      }

      .t-stat-item {
        padding: 8px 12px !important;
        min-width: 60px !important;
      }

      .t-stat-number {
        font-size: 16px !important;
      }

      .t-card-padding {
        padding: 12px !important;
      }

      .t-step-number {
        width: 28px !important;
        height: 28px !important;
        font-size: 11px !important;
      }

      .t-step-connector {
        width: 12px !important;
      }

      .t-period-card {
        padding: 16px 12px !important;
      }

      .t-period-price {
        font-size: 22px !important;
      }

      .t-order-summary {
        padding: 16px !important;
      }

      .t-total-amount {
        font-size: 20px !important;
      }
    }

    /* New card interaction styles */
    .t-select-radio {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
    }

    .t-fee-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 14px 16px;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.18s;
    }

    .t-fee-row:hover {
      transform: translateX(2px);
    }

    .t-select-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      width: 100%;
      padding: 10px 0;
      border-radius: 10px;
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.2s;
      border: 1.5px solid transparent;
    }
  `}</style>
);

const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  success: "#10B981",
};

/* ─── STEPS ──────────────────────────────────────────────────────── */

const steps = [
  {
    number: 1,
    title: "Personal Info",
    icon: RiUserLine,
  },
  {
    number: 2,
    title: "Select Pass & Stay",
    icon: RiTicketLine,
  },
  {
    number: 3,
    title: "Confirm",
    icon: RiCheckLine,
  },
  {
    number: 4,
    title: "Complete",
    icon: RiCheckLine,
  },
];

const defaultAccommodationOption = {
  value: "none",
  label: "No accommodation needed",
  price: 0,
  icon: RiHome2Line,
  description: "Arrange your own accommodation",
  features: [],
};

/* ─── HELPERS ────────────────────────────────────────────────────── */

const inputStyle = (focused) => ({
  width: "100%",
  padding: "clamp(10px,1.2vw,12px) clamp(12px,1.5vw,16px)",
  background: T.white,
  border: `1px solid ${focused ? T.primary : T.border}`,
  borderRadius: 10,
  fontSize: "clamp(13px,1vw,14px)",
  color: T.text,
  outline: "none",
  transition: "all 0.2s",
  fontFamily: "'Inter', sans-serif",
});

const iconInputStyle = (focused) => ({
  ...inputStyle(focused),
  paddingLeft: 38,
});

function SectionLabel({ label }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 16,
      }}
    >
      <span
        style={{
          fontSize: "clamp(10px,0.8vw,11px)",
          fontWeight: 700,
          color: T.primary,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>

      <div
        style={{
          flex: 1,
          height: 1,
          background: T.borderLight,
        }}
      />
    </div>
  );
}

function StepIndicator({ currentStep }) {
  return (
    <div
      className="t-steps-wrap"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "clamp(32px,5vw,48px)",
        flexWrap: "wrap",
        gap: "clamp(2px,0.5vw,4px)",
      }}
    >
      {steps.map((step, index) => (
        <React.Fragment key={step.number}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "clamp(4px,0.8vw,8px)",
            }}
          >
            <div
              className="t-step-number"
              style={{
                width: "clamp(32px,4vw,40px)",
                height: "clamp(32px,4vw,40px)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 700,
                fontSize: "clamp(12px,1.1vw,14px)",
                transition: "all 0.3s",
                background:
                  currentStep >= step.number
                    ? T.gradPrimary
                    : T.white,
                color:
                  currentStep >= step.number
                    ? T.white
                    : T.textMuted,
                border: `1px solid ${
                  currentStep >= step.number
                    ? "transparent"
                    : T.border
                }`,
                boxShadow:
                  currentStep >= step.number
                    ? T.shadow
                    : "none",
                flexShrink: 0,
              }}
            >
              {currentStep > step.number ? (
                <RiCheckLine size="clamp(14px,1.5vw,18px)" />
              ) : (
                step.number
              )}
            </div>

            <span
              style={{
                fontSize: "clamp(10px,0.8vw,12px)",
                fontWeight:
                  currentStep >= step.number
                    ? 600
                    : 400,
                color:
                  currentStep >= step.number
                    ? T.text
                    : T.textMuted,
                display: index < 2 ? "inline" : "none",
              }}
            >
              {step.title}
            </span>
          </div>

          {index < steps.length - 1 && (
            <div
              className="t-step-connector"
              style={{
                width: "clamp(16px,3vw,40px)",
                height: 2,
                background:
                  currentStep > step.number
                    ? T.primary
                    : T.border,
                transition: "background 0.3s",
                margin: "0 clamp(2px,0.5vw,4px)",
              }}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ─── RADIO INDICATOR ───────────────────────────────────────────── */

function SelectRadio({ selected, color }) {
  return (
    <div
      className="t-select-radio"
      style={{
        border: `2px solid ${
          selected ? color : T.border
        }`,
        background: selected ? color : T.white,
      }}
    >
      {selected && (
        <RiCheckLine
          size={12}
          color={T.white}
        />
      )}
    </div>
  );
}

/* ─── CURRENT PRICING PERIOD ────────────────────────────────────── */

// A tier stays open until the end of its deadline day. A tier with no
// deadline configured is skipped; Standard is the fallback once the
// earlier deadlines have passed.
function getCurrentPeriodKey(feeData, now = new Date()) {
  const isOpen = (deadline) => {
    if (!deadline) return false;
    const end = new Date(deadline);
    if (isNaN(end.getTime())) return false;
    end.setHours(23, 59, 59, 999);
    return now <= end;
  };

  if (isOpen(feeData?.superEarlyDeadline)) return "superEarly";
  if (isOpen(feeData?.earlyDeadline)) return "early";
  return "regular";
}

function formatDeadline(deadline) {
  if (!deadline) return null;
  const d = new Date(deadline);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/* ─── PERIOD CARD ───────────────────────────────────────────────── */

function PeriodCard({
  period,
  categories,
  selectedCategory,
  selectedPeriod,
  onSelect,
  recommended,
  disabled,
  statusText,
}) {
  const [hovered, setHovered] = useState(false);

  const isPeriodActive =
    !disabled && selectedPeriod === period.key;

  const periodColors = {
    superEarly: {
      border: T.accentGold,
      soft: "#D4A01712",
      label: "Super Earlybird",
      tag: "Best value",
    },

    early: {
      border: T.accentGoldLight,
      soft: "#E8B52012",
      label: "Earlybird",
      tag: "Limited time",
    },

    regular: {
      border: T.primary,
      soft: "#2A9D8F12",
      label: "Standard",
      tag: "Always open",
    },
  };

  const color =
    periodColors[period.key] ||
    periodColors.regular;

  return (
    <motion.div
      initial={{
        opacity: 0,
        y: 20,
      }}
      animate={{
        opacity: 1,
        y: 0,
      }}
      transition={{
        duration: 0.4,
        delay: period.index * 0.1,
      }}
      onMouseEnter={() =>
        !disabled && setHovered(true)
      }
      onMouseLeave={() =>
        setHovered(false)
      }
      className="t-period-card"
      aria-disabled={disabled}
      style={{
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? "none" : "auto",
        cursor: disabled ? "not-allowed" : "default",
        background: isPeriodActive
          ? color.soft
          : T.white,

        borderRadius:
          "clamp(16px,2vw,20px)",

        border: `2px solid ${
          isPeriodActive
            ? color.border
            : T.border
        }`,

        transition: "all 0.25s",

        transform: hovered
          ? "translateY(-4px)"
          : "translateY(0)",

        boxShadow: isPeriodActive
          ? T.shadowMd
          : T.shadow,

        position: "relative",

        padding:
          "clamp(20px,2.5vw,26px) clamp(16px,2vw,22px)",

        display: "flex",
        flexDirection: "column",
      }}
    >
      {recommended && (
        <div
          style={{
            position: "absolute",
            top: -12,
            left: "50%",
            transform:
              "translateX(-50%)",
            background: T.gradGold,
            color: T.navyDeep,
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            padding: "4px 14px",
            borderRadius: 20,
            boxShadow: T.shadowGold,
            whiteSpace: "nowrap",
          }}
        >
          Current Rate
        </div>
      )}

      <div
        style={{
          textAlign: "center",
          marginBottom: 18,
        }}
      >
        <h3
          style={{
            fontFamily:
              "'Playfair Display', serif",
            fontSize:
              "clamp(18px,2vw,21px)",
            fontWeight: 800,
            color: isPeriodActive
              ? color.border
              : T.text,
          }}
        >
          {color.label}
        </h3>

        <span
          style={{
            fontSize: 11,
            fontWeight: statusText ? 700 : 600,
            color: statusText
              ? "#DC2626"
              : T.textMuted,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {statusText || color.tag}
        </span>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          flex: 1,
        }}
      >
        {categories.map((cat) => {
          const price =
            cat[period.priceKey];

          if (
            price === undefined ||
            price === null
          ) {
            return null;
          }

          const isRowSelected =
            selectedCategory === cat.name &&
            isPeriodActive;

          return (
            <div
              key={cat.name}
              className="t-fee-row"
              onClick={() =>
                !disabled &&
                onSelect(
                  cat.name,
                  period.key
                )
              }
              style={{
                background: isRowSelected
                  ? T.white
                  : "transparent",

                border: `1.5px solid ${
                  isRowSelected
                    ? color.border
                    : "transparent"
                }`,

                boxShadow: isRowSelected
                  ? T.shadow
                  : "none",
              }}
              onMouseEnter={(e) => {
                if (!isRowSelected) {
                  e.currentTarget.style.background =
                    T.offWhite;
                }
              }}
              onMouseLeave={(e) => {
                if (!isRowSelected) {
                  e.currentTarget.style.background =
                    "transparent";
                }
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <SelectRadio
                  selected={isRowSelected}
                  color={color.border}
                />

                <span
                  style={{
                    fontSize:
                      "clamp(13px,1.1vw,14px)",
                    fontWeight:
                      isRowSelected
                        ? 700
                        : 500,
                    color: T.text,
                  }}
                >
                  {cat.name}
                </span>
              </div>

              <span
                style={{
                  fontSize:
                    "clamp(15px,1.3vw,17px)",
                  fontWeight: 800,
                  color: isRowSelected
                    ? color.border
                    : T.textMid,
                }}
              >
                ${price}
              </span>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

/* ─── ACCOMMODATION CARD ───────────────────────────────────────── */

function AccommodationCard({
  accommodation,
  isSelected,
  onSelect,
  index,
}) {
  const [hovered, setHovered] =
    useState(false);

  const Icon =
    accommodation.icon ||
    RiHotelLine;

  const accent =
    accommodation.value === "none"
      ? T.primary
      : accommodation.color ||
        T.primaryLight;

  return (
    <motion.div
      initial={{
        opacity: 0,
        y: 20,
      }}
      animate={{
        opacity: 1,
        y: 0,
      }}
      transition={{
        duration: 0.4,
        delay: index * 0.08,
      }}
      onMouseEnter={() =>
        setHovered(true)
      }
      onMouseLeave={() =>
        setHovered(false)
      }
      className="t-accom-card"
      style={{
        background: isSelected
          ? `${accent}0D`
          : T.white,

        borderRadius:
          "clamp(14px,1.8vw,16px)",

        border: `2px solid ${
          isSelected
            ? accent
            : T.border
        }`,

        transition: "all 0.25s",

        transform: hovered
          ? "translateY(-3px)"
          : "translateY(0)",

        position: "relative",

        padding:
          "clamp(16px,2vw,22px)",

        boxShadow: isSelected
          ? T.shadowMd
          : T.shadow,

        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent:
            "space-between",
          marginBottom: 12,
        }}
      >
        <div
          style={{
            width:
              "clamp(40px,4.5vw,46px)",
            height:
              "clamp(40px,4.5vw,46px)",
            borderRadius: 10,
            background: `${accent}12`,
            border: `1px solid ${accent}30`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon
            size="clamp(18px,2vw,22px)"
            color={accent}
          />
        </div>

        <SelectRadio
          selected={isSelected}
          color={accent}
        />
      </div>

      <div
        style={{
          fontFamily:
            "'Playfair Display', serif",
          fontSize:
            "clamp(16px,1.4vw,17px)",
          fontWeight: 800,
          color: T.text,
          marginBottom: 4,
        }}
      >
        {accommodation.label}
      </div>

      {accommodation.price > 0 && (
        <div
          style={{
            fontSize:
              "clamp(18px,2vw,22px)",
            fontWeight: 800,
            color: accent,
            marginBottom: 8,
          }}
        >
          ${accommodation.price}

          <span
            style={{
              fontSize:
                "clamp(10px,0.8vw,12px)",
              fontWeight: 400,
              color: T.textMuted,
            }}
          >
            {" "}
            / night
          </span>
        </div>
      )}

      {accommodation.description && (
        <p
          style={{
            fontSize:
              "clamp(12px,1vw,13px)",
            color: T.textMuted,
            marginBottom: 12,
            lineHeight: 1.6,
          }}
        >
          {accommodation.description}
        </p>
      )}

      {accommodation.features &&
        accommodation.features.length >
          0 && (
          <div
            style={{
              marginBottom: 14,
              textAlign: "left",
            }}
          >
            {accommodation.features
              .slice(0, 3)
              .map(
                (feature, idx) => (
                  <div
                    key={idx}
                    style={{
                      fontSize:
                        "clamp(11px,0.8vw,12px)",
                      color: T.textMuted,
                      padding: "2px 0",
                      display: "flex",
                      alignItems:
                        "center",
                      gap: "4px",
                    }}
                  >
                    <span
                      style={{
                        color: accent,
                      }}
                    >
                      ✓
                    </span>

                    {feature}
                  </div>
                )
              )}

            {accommodation.features
              .length > 3 && (
              <div
                style={{
                  fontSize:
                    "clamp(10px,0.7vw,11px)",
                  color: T.textMuted,
                  fontStyle: "italic",
                  marginTop: 4,
                }}
              >
                +
                {accommodation.features
                  .length - 3}{" "}
                more benefits
              </div>
            )}
          </div>
        )}

      <button
        type="button"
        onClick={() =>
          onSelect(
            accommodation.value
          )
        }
        className="t-select-btn"
        style={{
          marginTop: "auto",
          background: isSelected
            ? accent
            : "transparent",
          borderColor: accent,
          color: isSelected
            ? T.white
            : accent,
        }}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.background =
              `${accent}12`;
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            e.currentTarget.style.background =
              "transparent";
          }
        }}
      >
        {isSelected ? (
          <>
            <RiCheckLine size={15} />
            Selected
          </>
        ) : (
          "Select"
        )}
      </button>
    </motion.div>
  );
}

/* ─── NIGHT COUNTER ─────────────────────────────────────────────── */

function NightCounter({
  nights,
  onNightsChange,
  minNights = 1,
  maxNights = 7,
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "clamp(10px,1.5vw,14px)",
        flexWrap: "wrap",
      }}
    >
      <button
        type="button"
        onClick={() =>
          nights > minNights &&
          onNightsChange(nights - 1)
        }
        disabled={
          nights <= minNights
        }
        style={{
          width:
            "clamp(36px,4vw,40px)",
          height:
            "clamp(36px,4vw,40px)",
          borderRadius: 10,
          background:
            nights <= minNights
              ? T.offWhite
              : T.primary,
          border: `1px solid ${
            nights <= minNights
              ? T.border
              : T.primary
          }`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor:
            nights <= minNights
              ? "not-allowed"
              : "pointer",
          opacity:
            nights <= minNights
              ? 0.5
              : 1,
          transition: "all 0.2s",
        }}
      >
        <RiSubtractLine
          size="clamp(14px,1.2vw,16px)"
          color={
            nights <= minNights
              ? T.textMuted
              : T.white
          }
        />
      </button>

      <div
        style={{
          minWidth:
            "clamp(60px,8vw,80px)",
          textAlign: "center",
          fontSize:
            "clamp(16px,1.5vw,18px)",
          fontWeight: 700,
          color: T.text,
        }}
      >
        {nights}{" "}
        {nights === 1
          ? "Night"
          : "Nights"}
      </div>

      <button
        type="button"
        onClick={() =>
          nights < maxNights &&
          onNightsChange(nights + 1)
        }
        disabled={
          nights >= maxNights
        }
        style={{
          width:
            "clamp(36px,4vw,40px)",
          height:
            "clamp(36px,4vw,40px)",
          borderRadius: 10,
          background:
            nights >= maxNights
              ? T.offWhite
              : T.primary,
          border: `1px solid ${
            nights >= maxNights
              ? T.border
              : T.primary
          }`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor:
            nights >= maxNights
              ? "not-allowed"
              : "pointer",
          opacity:
            nights >= maxNights
              ? 0.5
              : 1,
          transition: "all 0.2s",
        }}
      >
        <RiAddLine
          size="clamp(14px,1.2vw,16px)"
          color={
            nights >= maxNights
              ? T.textMuted
              : T.white
          }
        />
      </button>
    </div>
  );
}

/* ─── FIELD ─────────────────────────────────────────────────────── */

function Field({
  label,
  required,
  children,
}) {
  return (
    <div>
      <label
        style={{
          display: "block",
          fontSize:
            "clamp(12px,1vw,13px)",
          fontWeight: 600,
          color: T.text,
          marginBottom: 6,
        }}
      >
        {label}{" "}
        {required && (
          <span
            style={{
              color: T.primary,
            }}
          >
            *
          </span>
        )}
      </label>

      {children}
    </div>
  );
}

/* ─── FOCUS INPUT ───────────────────────────────────────────────── */

function FocusInput({
  icon: Icon,
  ...props
}) {
  const [focused, setFocused] =
    useState(false);

  return (
    <div
      style={{
        position: "relative",
      }}
    >
      {Icon && (
        <Icon
          style={{
            position: "absolute",
            left:
              "clamp(10px,1.2vw,14px)",
            top: "50%",
            transform:
              "translateY(-50%)",
            color: focused
              ? T.primary
              : T.textMuted,
            fontSize:
              "clamp(14px,1.2vw,16px)",
            pointerEvents: "none",
          }}
        />
      )}

      <input
        {...props}
        style={
          Icon
            ? iconInputStyle(focused)
            : inputStyle(focused)
        }
        onFocus={() =>
          setFocused(true)
        }
        onBlur={() =>
          setFocused(false)
        }
      />
    </div>
  );
}

/* ─── MAIN COMPONENT ────────────────────────────────────────────── */

export default function Registration() {
  const { id: slug } = useParams();

  /* ─── State ───────────────────────────────────────────────────── */

  const [event, setEvent] =
    useState(null);

  const [loadingEvent, setLoadingEvent] =
    useState(true);

  const [eventError, setEventError] =
    useState(null);

  const [step, setStep] = useState(1);

  const [loading, setLoading] =
    useState(false);

  const [feeData, setFeeData] =
    useState(null);

  const [loadingFee, setLoadingFee] =
    useState(true);

  const [feeError, setFeeError] =
    useState(null);

  const [
    accommodations,
    setAccommodations,
  ] = useState([]);

  const [
    loadingAccommodations,
    setLoadingAccommodations,
  ] = useState(true);

  const [formData, setFormData] =
    useState({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      company: "",
      jobTitle: "",
      summitType: "",
      selectedPeriod: "regular",
      accommodation: "none",
      nights: 3,
      specialRequirements: "",
      agreeToTerms: false,
      eventId: "",
      eventTitle: "",
    });

  /* ─── Fetch Event ─────────────────────────────────────────────── */

  useEffect(() => {
    const fetchEvent = async () => {
      if (!slug) {
        setLoadingEvent(false);
        setEventError(
          "No event identifier provided."
        );
        return;
      }

      try {
        const token =
          localStorage.getItem(
            "token"
          );

        const response =
          await fetch(
            `${API_BASE}/api/events/${slug}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

        const data =
          await response.json();

        if (
          data.success &&
          data.event
        ) {
          setEvent(data.event);

          setFormData((prev) => ({
            ...prev,
            eventId:
              data.event._id,
            eventTitle:
              data.event.title,
          }));
        } else if (
          data.event
        ) {
          setEvent(data.event);

          setFormData((prev) => ({
            ...prev,
            eventId:
              data.event._id,
            eventTitle:
              data.event.title,
          }));
        } else {
          setEventError(
            data.message ||
              "Event not found."
          );
        }
      } catch (error) {
        console.error(
          "Error fetching event:",
          error
        );

        setEventError(
          "Failed to load event details."
        );
      } finally {
        setLoadingEvent(false);
      }
    };

    fetchEvent();
  }, [slug]);

  /* ─── Fetch Registration Fee ─────────────────────────────────── */

  useEffect(() => {
    const fetchRegistrationFee =
      async () => {
        if (!slug) {
          setLoadingFee(false);
          return;
        }

        setLoadingFee(true);
        setFeeError(null);

        try {
          const res =
            await axios.get(
              `/api/registration-fee/active/${slug}`
            );

          if (
            res.data.success &&
            res.data.data
          ) {
            setFeeData(
              res.data.data
            );

            if (
              res.data.data.categories &&
              res.data.data.categories
                .length > 0
            ) {
              const first =
                res.data.data
                  .categories[0];

              setFormData(
                (prev) => ({
                  ...prev,
                  summitType:
                    first.name,
                  selectedPeriod:
                    getCurrentPeriodKey(
                      res.data.data
                    ),
                })
              );
            }
          } else {
            setFeeError(
              res.data.message ||
                "No active registration fee found."
            );
          }
        } catch (error) {
          console.error(
            "Error fetching registration fee:",
            error
          );

          setFeeError(
            "Failed to load registration fees."
          );
        } finally {
          setLoadingFee(false);
        }
      };

    fetchRegistrationFee();
  }, [slug]);

  /* ─── Fetch Accommodations ───────────────────────────────────── */

  useEffect(() => {
    const fetchAccommodations =
      async () => {
        const eventId =
          formData.eventId;

        if (!eventId) {
          setAccommodations([
            defaultAccommodationOption,
          ]);

          setLoadingAccommodations(
            false
          );

          return;
        }

        setLoadingAccommodations(
          true
        );

        try {
          const token =
            localStorage.getItem(
              "token"
            );

          const response =
            await axios.get(
              `${API_BASE}/api/accommodation/event/${eventId}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );

          if (
            response.data.success &&
            response.data.data?.length >
              0
          ) {
            const formatted = [
              defaultAccommodationOption,

              ...response.data.data.map(
                (acc) => ({
                  value: acc._id,
                  label: acc.title,
                  price: acc.price,
                  description:
                    acc.description ||
                    `${acc.title} room`,
                  features:
                    acc.features || [],
                  color:
                    acc.color ||
                    T.primaryLight,
                  icon: RiHotelLine,
                })
              ),
            ];

            setAccommodations(
              formatted
            );
          } else {
            setAccommodations([
              defaultAccommodationOption,
            ]);
          }
        } catch (error) {
          console.error(
            "Error fetching accommodations:",
            error
          );

          toast.error(
            "Failed to load accommodation options"
          );

          setAccommodations([
            defaultAccommodationOption,
          ]);
        } finally {
          setLoadingAccommodations(
            false
          );
        }
      };

    fetchAccommodations();
  }, [formData.eventId]);

  /* ─── Prepare data for period cards ──────────────────────────── */

  const categories =
    feeData?.categories || [];

  const periods = [
    {
      key: "superEarly",
      priceKey: "superEarlyPrice",
      label: "Super Earlybird",
    },
    {
      key: "early",
      priceKey: "earlyPrice",
      label: "Earlybird",
    },
    {
      key: "regular",
      priceKey: "regularPrice",
      label: "Standard",
    },
  ];

  const currentPeriodKey =
    getCurrentPeriodKey(feeData);

  const periodIndex = (key) =>
    periods.findIndex(
      (p) => p.key === key
    );

  const getPeriodStatusText = (
    period
  ) => {
    const deadlineKey = {
      superEarly: "superEarlyDeadline",
      early: "earlyDeadline",
      regular: "regularDeadline",
    }[period.key];
    const deadline = formatDeadline(
      feeData?.[deadlineKey]
    );

    if (period.key === currentPeriodKey) {
      return deadline
        ? `Ends ${deadline}`
        : null;
    }
    if (
      periodIndex(period.key) <
      periodIndex(currentPeriodKey)
    ) {
      return deadline
        ? `Closed ${deadline}`
        : "Closed";
    }
    return "Opens later";
  };

  /* ─── Selected pass details ──────────────────────────────────── */

  const selectedCategory =
    categories.find(
      (c) =>
        c.name === formData.summitType
    );

  // Only the current tier can be purchased, whatever is in formData.
  const selectedPeriodKey =
    currentPeriodKey;

  const selectedPeriod =
    periods.find(
      (p) =>
        p.key === selectedPeriodKey
    );

  const passPrice =
    selectedCategory
      ? selectedCategory[
          selectedPeriod?.priceKey
        ]
      : 0;

  const selectedAccom =
    accommodations.find(
      (a) =>
        a.value ===
        formData.accommodation
    );

  const accomPrice =
    (selectedAccom?.price || 0) *
    formData.nights;

  const subtotal =
    passPrice +
    (formData.accommodation !==
    "none"
      ? accomPrice
      : 0);

  /*
   * IMPORTANT:
   * 5% PROCESSING FEE IS RETAINED.
   * DO NOT CHANGE THIS TO TAX.
   */
  const processingFee =
    subtotal * 0.05;

  const totalAmount =
    subtotal + processingFee;

  /* ─── Handlers ───────────────────────────────────────────────── */

  const handleChange = (e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  };

  const handlePeriodCategorySelect =
    (
      categoryName,
      periodKey
    ) => {
      if (periodKey !== currentPeriodKey) return;

      setFormData((prev) => ({
        ...prev,
        summitType:
          categoryName,
        selectedPeriod:
          periodKey,
      }));
    };

  /* ─── Validate Step 1 ────────────────────────────────────────── */

  const validateStep1 = () => {
    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.phone
    ) {
      toast.error(
        "Please fill all required fields"
      );

      return false;
    }

    if (
      !formData.email.includes("@")
    ) {
      toast.error(
        "Please enter a valid email"
      );

      return false;
    }

    return true;
  };

  /* ─── Submit ──────────────────────────────────────────────────── */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (step === 1) {
      if (!validateStep1()) {
        return;
      }

      setStep(2);

      return;
    }

    if (step === 2) {
      if (!formData.summitType) {
        toast.error(
          "Please select a pass category"
        );

        return;
      }

      setStep(3);

      return;
    }

    if (step === 3) {
      if (!formData.agreeToTerms) {
        toast.error(
          "Please agree to Terms & Conditions"
        );

        return;
      }

      setLoading(true);

      let token = localStorage.getItem("token");

      try {
        /* ── STEP A0: Silently get (or create) an attendee account so we
         * have a valid token to authenticate the payment/registration
         * calls below. The registration form never collects a password,
         * so this doesn't require one. ── */

        const sessionRes = await axios.post(
          `${API_BASE}/api/auth/event-session`,
          {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            company: formData.company,
            jobTitle: formData.jobTitle,
            summitType: formData.summitType,
          }
        );

        if (!sessionRes.data.success) {
          throw new Error(
            sessionRes.data.message ||
              "Could not verify your details"
          );
        }

        token = sessionRes.data.token;
        localStorage.setItem("token", token);

        /* ── STEP A: Create a real Razorpay order on the server ── */

        const orderRes = await axios.post(
          `${API_BASE}/api/payments/create-order`,
          {
            amount: totalAmount,
            currency: "USD",
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!orderRes.data.success) {
          throw new Error(
            orderRes.data.message ||
              "Could not create payment order"
          );
        }

        const {
          orderId,
          amount: rzpAmount,
          currency: rzpCurrency,
          keyId,
          paymentId: dbPaymentId,
        } = orderRes.data;

        /* ── STEP B: Load Razorpay Checkout script ── */

        const scriptLoaded = await loadRazorpayScript();

        if (!scriptLoaded) {
          throw new Error(
            "Razorpay SDK failed to load. Please check your internet connection."
          );
        }

        /* ── STEP C: Open Razorpay Checkout modal ── */

        const options = {
          key: keyId,
          amount: rzpAmount,
          currency: rzpCurrency,
          order_id: orderId,
          name: conferenceName,
          description: `${selectedCategory?.name || "Pass"} - ${
            selectedPeriod?.label || ""
          }`,
          prefill: {
            name: `${formData.firstName} ${formData.lastName}`,
            email: formData.email,
            contact: formData.phone,
          },
          theme: {
            color: "#2A9D8F",
          },

          handler: async function (response) {
            try {
              /* ── STEP D: Verify signature server-side ── */

              const verifyRes = await axios.post(
                `${API_BASE}/api/payments/verify`,
                {
                  razorpay_order_id:
                    response.razorpay_order_id,
                  razorpay_payment_id:
                    response.razorpay_payment_id,
                  razorpay_signature:
                    response.razorpay_signature,
                },
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                }
              );

              if (!verifyRes.data.success) {
                throw new Error(
                  verifyRes.data.message ||
                    "Payment verification failed"
                );
              }

              /* ── STEP E: Create the registration, linked to the verified payment ── */

              const registrationData = {
                eventId: formData.eventId,

                eventName:
                  formData.eventTitle ||
                  feeData?.conferenceName ||
                  "Event",

                firstName: formData.firstName,

                lastName: formData.lastName,

                email: formData.email,

                phone: formData.phone,

                company: formData.company,

                jobTitle: formData.jobTitle,

                summitType: formData.summitType,

                summitTitle: selectedCategory?.name,

                summitPrice: passPrice,

                selectedPeriod: selectedPeriodKey,

                accommodation: formData.accommodation,

                accommodationTitle: selectedAccom?.label,

                accommodationPrice:
                  selectedAccom?.price || 0,

                nights: formData.nights,

                specialRequirements:
                  formData.specialRequirements || "",

                subtotalAmount: subtotal,

                /*
                 * 5% PROCESSING FEE
                 */
                processingFee: processingFee,

                totalAmount: totalAmount,

               paymentId: verifyRes.data.paymentId || dbPaymentId,
              };

              const res = await axios.post(
                `${API_BASE}/api/registrations`,
                registrationData,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                }
              );

              if (res.data.success) {
                toast.success(
                  `Registration for "${
                    formData.eventTitle ||
                    feeData?.conferenceName ||
                    "Event"
                  }" successful! 🎉`
                );

                setStep(4);
              } else {
                throw new Error(
                  res.data.message ||
                    "Registration failed"
                );
              }
            } catch (err) {
              console.error(
                "Post-payment error:",
                err
              );

              toast.error(
                err.response?.data?.message ||
                  err.message ||
                  "Something went wrong after payment. Please contact support with your payment ID."
              );
            } finally {
              setLoading(false);
            }
          },

          modal: {
            ondismiss: async function () {
              setLoading(false);

              try {
                await axios.post(
                  `${API_BASE}/api/payments/failed`,
                  {
                    razorpay_order_id: orderId,
                    error: "Payment cancelled by user",
                  },
                  {
                    headers: {
                      Authorization: `Bearer ${token}`,
                    },
                  }
                );
              } catch (e) {
                console.error(
                  "Failed to record cancelled payment:",
                  e
                );
              }

              toast.error("Payment cancelled");
            },
          },
        };

        const rzp = new window.Razorpay(options);

        rzp.on("payment.failed", async function (response) {
          setLoading(false);

          try {
            await axios.post(
              `${API_BASE}/api/payments/failed`,
              {
                razorpay_order_id:
                  response.error.metadata.order_id,
                error: response.error.description,
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
          } catch (e) {
            console.error(
              "Failed to record failed payment:",
              e
            );
          }

          toast.error(
            response.error.description ||
              "Payment failed. Please try again."
          );
        });

        rzp.open();

        /*
         * Note: setLoading(false) is intentionally NOT called here.
         * It is handled inside handler(), modal.ondismiss(), and
         * the payment.failed listener above, once the outcome
         * of the checkout is known.
         */
      } catch (err) {
        console.error(
          "Payment initiation error:",
          err
        );

        toast.error(
          err.response?.data?.message ||
            err.message ||
            "Could not start payment. Please try again."
        );

        setLoading(false);
      }
    }
  };

  /* ─── Determine conference name ──────────────────────────────── */

  const conferenceName =
    formData.eventTitle ||
    feeData?.conferenceName ||
    "Conference";

  const location =
    feeData?.location || "";

  const currency =
    feeData?.currency || "USD";

  /* ─── Loading state ──────────────────────────────────────────── */

  if (
    loadingEvent ||
    loadingFee ||
    loadingAccommodations
  ) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: T.offWhite,
          display: "flex",
          alignItems: "center",
          justifyContent:
            "center",
        }}
      >
        <FontImport />

        <div
          style={{
            width:
              "clamp(36px,4vw,44px)",
            height:
              "clamp(36px,4vw,44px)",
            border: `3px solid ${T.border}`,
            borderTopColor:
              T.primary,
            borderRadius: "50%",
            animation:
              "spin 1s linear infinite",
          }}
        />
      </div>
    );
  }

  /* ─── Event error ────────────────────────────────────────────── */

  if (eventError && !event) {
    return (
      <div
        style={{
          maxWidth: 600,
          margin: "40px auto",
          padding: "0 20px",
        }}
      >
        <div
          className="t-card"
          style={{
            borderColor:
              "#EF4444",
            backgroundColor:
              "#FEF2F2",
          }}
        >
          <h2
            style={{
              color: "#EF4444",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            Event Not Found
          </h2>

          <p>
            {eventError}
          </p>

          <button
            onClick={() =>
              window.location.reload()
            }
            className="t-btn-outline"
            style={{
              marginTop: 16,
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  /* ─── Render ─────────────────────────────────────────────────── */

  return (
    <div
      style={{
        minHeight: "100vh",
        background: T.offWhite,
        fontFamily:
          "'Inter', sans-serif",
        color: T.text,
      }}
    >
      <FontImport />

      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: T.white,
            color: T.text,
            border: `1px solid ${T.border}`,
            borderRadius: 10,
            fontSize: 13,
          },
        }}
      />

      {/* ── HERO SECTION ── */}

      <section
        className="t-hero-padding"
        style={{
          padding:
            "clamp(32px,6vw,80px) clamp(16px,4vw,64px) clamp(32px,5vw,64px)",

          background:
            T.gradSoft,

          borderBottom:
            `1px solid ${T.border}`,

          position: "relative",

          overflow: "hidden",
        }}
      >
        <div
          style={{
            maxWidth: 1320,
            margin: "0 auto",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap:
                "clamp(6px,0.8vw,8px)",
              marginBottom:
                "clamp(14px,2vw,20px)",
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                padding:
                  "clamp(4px,0.6vw,5px) clamp(10px,1.2vw,14px)",
                borderRadius: 20,
                background:
                  T.softTeal,
                border:
                  `1px solid ${T.border}`,
                color: T.primary,
                fontSize:
                  "clamp(10px,0.8vw,11px)",
                fontWeight: 700,
                textTransform:
                  "uppercase",
                letterSpacing:
                  "0.08em",
              }}
            >
              <RiUserAddLine
                size="clamp(11px,0.9vw,13px)"
                style={{
                  display: "inline",
                  marginRight: 5,
                }}
              />

              {`Register for: ${conferenceName}`}
            </span>
          </div>

          <h1
            className="t-hero-title"
            style={{
              fontFamily:
                "'Playfair Display', serif",
              fontSize:
                "clamp(1.6rem,4.5vw,3.4rem)",
              fontWeight: 900,
              color: T.navy,
              letterSpacing:
                "-0.03em",
              lineHeight: 1.06,
              marginBottom:
                "clamp(10px,1.5vw,14px)",
            }}
          >
            {conferenceName}{" "}
            <em
              style={{
                color:
                  T.accentGold,
                fontStyle: "normal",
              }}
            >
              Registration
            </em>
          </h1>

          {location && (
            <p
              style={{
                fontSize:
                  "clamp(0.85rem,1.3vw,1rem)",
                color: T.textMid,
                lineHeight: 1.7,
                maxWidth: 560,
              }}
            >
              📍 {location} · Currency:{" "}
              {currency}
            </p>
          )}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap:
                "clamp(8px,1vw,12px)",
              marginTop:
                "clamp(16px,2.5vw,24px)",
            }}
          >
            <div
              style={{
                flex: 1,
                height: 2,
                maxWidth: 60,
                background:
                  `linear-gradient(90deg, transparent, ${T.primary}40, transparent)`,
              }}
            />

            <div
              style={{
                width:
                  "clamp(6px,0.7vw,8px)",
                height:
                  "clamp(6px,0.7vw,8px)",
                background:
                  T.accentGold,
                transform:
                  "rotate(45deg)",
              }}
            />

            <div
              style={{
                flex: 1,
                height: 2,
                maxWidth: 60,
                background:
                  `linear-gradient(90deg, transparent, ${T.primary}40, transparent)`,
              }}
            />
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}

      <div
        style={{
          maxWidth: 1320,
          margin: "0 auto",
          padding:
            "clamp(16px,3vw,24px) clamp(12px,4vw,40px) 0",
        }}
      >
        <div
          className="t-stats-wrap"
          style={{
            display: "flex",
            gap:
              "clamp(8px,1.5vw,16px)",
            flexWrap: "wrap",
            marginBottom:
              "clamp(16px,2.5vw,24px)",
          }}
        >
          <div
            className="t-stat-item"
            style={{
              background: T.white,
              borderRadius: 10,
              padding:
                "clamp(12px,2vw,16px) clamp(16px,2.5vw,24px)",
              border:
                `1px solid ${T.border}`,
              flex: "1 1 auto",
              minWidth:
                "clamp(80px,15vw,120px)",
              boxShadow: T.shadow,
            }}
          >
            <div
              className="t-stat-number"
              style={{
                fontSize:
                  "clamp(22px,3vw,28px)",
                fontWeight: 800,
                color:
                  T.accentGold,
              }}
            >
              {categories.length}
            </div>

            <div
              className="t-stat-label"
              style={{
                fontSize:
                  "clamp(10px,0.8vw,12px)",
                color:
                  T.textMuted,
              }}
            >
              Categories
            </div>
          </div>

          <div
            className="t-stat-item"
            style={{
              background: T.white,
              borderRadius: 10,
              padding:
                "clamp(12px,2vw,16px) clamp(16px,2.5vw,24px)",
              border:
                `1px solid ${T.border}`,
              flex: "1 1 auto",
              minWidth:
                "clamp(80px,15vw,120px)",
              boxShadow: T.shadow,
            }}
          >
            <div
              className="t-stat-number"
              style={{
                fontSize:
                  "clamp(22px,3vw,28px)",
                fontWeight: 800,
                color:
                  T.primary,
              }}
            >
              {accommodations.length -
                1}
            </div>

            <div
              className="t-stat-label"
              style={{
                fontSize:
                  "clamp(10px,0.8vw,12px)",
                color:
                  T.textMuted,
              }}
            >
              Hotels
            </div>
          </div>

          <div
            className="t-stat-item"
            style={{
              background: T.white,
              borderRadius: 10,
              padding:
                "clamp(12px,2vw,16px) clamp(16px,2.5vw,24px)",
              border:
                `1px solid ${T.border}`,
              flex: "1 1 auto",
              minWidth:
                "clamp(80px,15vw,120px)",
              boxShadow: T.shadow,
            }}
          >
            <div
              className="t-stat-number"
              style={{
                fontSize:
                  "clamp(22px,3vw,28px)",
                fontWeight: 800,
                color:
                  T.accentTealLight,
              }}
            >
              {periods.length}
            </div>

            <div
              className="t-stat-label"
              style={{
                fontSize:
                  "clamp(10px,0.8vw,12px)",
                color:
                  T.textMuted,
              }}
            >
              Pricing Periods
            </div>
          </div>
        </div>
      </div>

      {/* ── CONTENT ── */}

      <div
        style={{
          maxWidth: 1000,
          margin: "0 auto",
          padding:
            "0 clamp(12px,4vw,40px) clamp(40px,6vw,80px)",
        }}
      >
        <StepIndicator
          currentStep={step}
        />

        <div
          className="t-card-padding"
          style={{
            background: T.white,
            borderRadius:
              "clamp(20px,3vw,28px)",
            border:
              `1px solid ${T.border}`,
            padding:
              "clamp(20px,4vw,40px) clamp(16px,4vw,48px)",
            boxShadow:
              T.shadowLg,
          }}
        >
          {/* Event Name Banner */}

          {conferenceName && (
            <div
              style={{
                background:
                  T.softTeal,
                borderRadius:
                  "clamp(10px,1.2vw,12px)",
                padding:
                  "clamp(12px,1.5vw,16px) clamp(14px,2vw,20px)",
                marginBottom:
                  "clamp(16px,2.5vw,24px)",
                border:
                  `1px solid ${T.border}`,
                display: "flex",
                alignItems:
                  "center",
                gap:
                  "clamp(8px,1vw,12px)",
                flexWrap: "wrap",
              }}
            >
              <RiCalendarEventLine
                size="clamp(16px,1.5vw,20px)"
                color={T.primary}
              />

              <div>
                <div
                  style={{
                    fontSize:
                      "clamp(10px,0.8vw,12px)",
                    color:
                      T.textMuted,
                    fontWeight: 600,
                    letterSpacing:
                      "0.05em",
                    textTransform:
                      "uppercase",
                  }}
                >
                  Registering for
                </div>

                <div
                  style={{
                    fontSize:
                      "clamp(14px,1.2vw,16px)",
                    fontWeight: 700,
                    color: T.text,
                  }}
                >
                  {conferenceName}
                </div>
              </div>
            </div>
          )}

          <form
            onSubmit={handleSubmit}
          >
            <AnimatePresence
              mode="wait"
            >
              {/* ── STEP 1: PERSONAL INFO ── */}

              {step === 1 && (
                <motion.div
                  key="s1"
                  initial={{
                    opacity: 0,
                    x: 20,
                  }}
                  animate={{
                    opacity: 1,
                    x: 0,
                  }}
                  exit={{
                    opacity: 0,
                    x: -20,
                  }}
                  transition={{
                    duration: 0.35,
                  }}
                >
                  <h2
                    style={{
                      fontFamily:
                        "'Playfair Display', serif",
                      fontSize:
                        "clamp(22px,2.5vw,26px)",
                      fontWeight: 800,
                      color: T.text,
                      marginBottom: 4,
                    }}
                  >
                    Personal Information
                  </h2>

                  <p
                    style={{
                      color:
                        T.textMuted,
                      marginBottom:
                        "clamp(24px,3vw,32px)",
                      fontSize:
                        "clamp(13px,1.1vw,14px)",
                    }}
                  >
                    Please provide your
                    contact details for
                    registration
                  </p>

                  <div
                    style={{
                      display: "flex",
                      flexDirection:
                        "column",
                      gap:
                        "clamp(16px,2vw,22px)",
                    }}
                  >
                    <div
                      className="t-form-grid"
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "1fr 1fr",
                        gap:
                          "clamp(14px,2vw,20px)",
                      }}
                    >
                      <Field
                        label="First Name"
                        required
                      >
                        <FocusInput
                          type="text"
                          name="firstName"
                          value={
                            formData.firstName
                          }
                          onChange={
                            handleChange
                          }
                          placeholder="John"
                        />
                      </Field>

                      <Field
                        label="Last Name"
                        required
                      >
                        <FocusInput
                          type="text"
                          name="lastName"
                          value={
                            formData.lastName
                          }
                          onChange={
                            handleChange
                          }
                          placeholder="Doe"
                        />
                      </Field>
                    </div>

                    <Field
                      label="Email Address"
                      required
                    >
                      <FocusInput
                        icon={RiMailLine}
                        type="email"
                        name="email"
                        value={
                          formData.email
                        }
                        onChange={
                          handleChange
                        }
                        placeholder="john@example.com"
                      />
                    </Field>

                    <Field
                      label="Phone Number"
                      required
                    >
                      <FocusInput
                        icon={RiPhoneLine}
                        type="tel"
                        name="phone"
                        value={
                          formData.phone
                        }
                        onChange={
                          handleChange
                        }
                        placeholder="+1 (234) 567-8900"
                      />
                    </Field>

                    <div
                      className="t-form-grid"
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "1fr 1fr",
                        gap:
                          "clamp(14px,2vw,20px)",
                      }}
                    >
                      <Field label="Company">
                        <FocusInput
                          icon={
                            RiBuildingLine
                          }
                          type="text"
                          name="company"
                          value={
                            formData.company
                          }
                          onChange={
                            handleChange
                          }
                          placeholder="Company Inc."
                        />
                      </Field>

                      <Field label="Job Title">
                        <FocusInput
                          icon={
                            RiBriefcaseLine
                          }
                          type="text"
                          name="jobTitle"
                          value={
                            formData.jobTitle
                          }
                          onChange={
                            handleChange
                          }
                          placeholder="Software Engineer"
                        />
                      </Field>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* ── STEP 2: SELECT PASS & ACCOMMODATION ── */}

              {step === 2 && (
                <motion.div
                  key="s2"
                  initial={{
                    opacity: 0,
                    x: 20,
                  }}
                  animate={{
                    opacity: 1,
                    x: 0,
                  }}
                  exit={{
                    opacity: 0,
                    x: -20,
                  }}
                  transition={{
                    duration: 0.35,
                  }}
                >
                  <h2
                    style={{
                      fontFamily:
                        "'Playfair Display', serif",
                      fontSize:
                        "clamp(22px,2.5vw,26px)",
                      fontWeight: 800,
                      color: T.text,
                      marginBottom: 4,
                    }}
                  >
                    Select Pass &
                    Accommodation
                  </h2>

                  {/* Period Cards */}

                  <SectionLabel
                    label="Registration Fees"
                  />

                  <div
                    className="t-period-grid"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit,minmax(240px,1fr))",
                      gap:
                        "clamp(16px,2vw,24px)",
                    }}
                  >
                    {periods.map(
                      (
                        period,
                        idx
                      ) => (
                        <PeriodCard
                          key={
                            period.key
                          }
                          period={{
                            ...period,
                            index: idx,
                          }}
                          categories={
                            categories
                          }
                          selectedCategory={
                            formData.summitType
                          }
                          selectedPeriod={
                            selectedPeriodKey
                          }
                          onSelect={
                            handlePeriodCategorySelect
                          }
                          recommended={
                            period.key ===
                            currentPeriodKey
                          }
                          disabled={
                            period.key !==
                            currentPeriodKey
                          }
                          statusText={getPeriodStatusText(
                            period
                          )}
                        />
                      )
                    )}
                  </div>

                  {/* Accommodations */}

                  <div
                    style={{
                      marginTop:
                        "clamp(40px,5vw,56px)",
                    }}
                  >
                    <SectionLabel
                      label="Accommodation"
                    />

                    <div
                      className="t-accom-grid"
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit,minmax(220px,1fr))",
                        gap:
                          "clamp(12px,1.5vw,16px)",
                      }}
                    >
                      {accommodations.map(
                        (
                          acc,
                          i
                        ) => (
                          <AccommodationCard
                            key={
                              acc.value
                            }
                            accommodation={
                              acc
                            }
                            index={i}
                            isSelected={
                              formData.accommodation ===
                              acc.value
                            }
                            onSelect={(
                              val
                            ) =>
                              setFormData(
                                (
                                  prev
                                ) => ({
                                  ...prev,
                                  accommodation:
                                    val,
                                })
                              )
                            }
                          />
                        )
                      )}
                    </div>
                  </div>

                  {/* Nights selector */}

                  {formData.accommodation !==
                    "none" && (
                    <div
                      style={{
                        marginTop:
                          "clamp(20px,2.5vw,28px)",
                        padding:
                          "clamp(14px,2vw,20px) clamp(16px,2.5vw,24px)",
                        background:
                          T.offWhite,
                        borderRadius: 14,
                        border:
                          `1px solid ${T.border}`,
                      }}
                    >
                      <label
                        style={{
                          display:
                            "block",
                          fontSize:
                            "clamp(12px,1vw,13px)",
                          fontWeight: 600,
                          color:
                            T.text,
                          marginBottom:
                            "clamp(10px,1.2vw,14px)",
                        }}
                      >
                        Number of Nights
                      </label>

                      <NightCounter
                        nights={
                          formData.nights
                        }
                        onNightsChange={(
                          n
                        ) =>
                          setFormData(
                            (
                              prev
                            ) => ({
                              ...prev,
                              nights: n,
                            })
                          )
                        }
                      />
                    </div>
                  )}

                  {/* Special requirements */}

                  <div
                    style={{
                      marginTop:
                        "clamp(20px,2.5vw,24px)",
                    }}
                  >
                    <label
                      style={{
                        display:
                          "block",
                        fontSize:
                          "clamp(12px,1vw,13px)",
                        fontWeight: 600,
                        color:
                          T.text,
                        marginBottom: 6,
                      }}
                    >
                      Special Requirements{" "}
                      <span
                        style={{
                          color:
                            T.textMuted,
                        }}
                      >
                        (Optional)
                      </span>
                    </label>

                    <textarea
                      name="specialRequirements"
                      value={
                        formData.specialRequirements
                      }
                      onChange={
                        handleChange
                      }
                      rows="3"
                      placeholder="Dietary requirements, accessibility needs, or other requests…"
                      style={{
                        width: "100%",
                        padding:
                          "clamp(10px,1.2vw,12px) clamp(12px,1.5vw,16px)",
                        background:
                          T.white,
                        border:
                          `1px solid ${T.border}`,
                        borderRadius: 10,
                        fontSize:
                          "clamp(13px,1vw,14px)",
                        color:
                          T.text,
                        fontFamily:
                          "'Inter', sans-serif",
                        resize:
                          "vertical",
                        outline:
                          "none",
                        transition:
                          "border-color 0.2s",
                      }}
                      onFocus={(e) =>
                        (e.currentTarget.style.borderColor =
                          T.primary)
                      }
                      onBlur={(e) =>
                        (e.currentTarget.style.borderColor =
                          T.border)
                      }
                    />
                  </div>
                </motion.div>
              )}

              {/* ── STEP 3: CONFIRM ── */}

              {step === 3 &&
                selectedCategory &&
                selectedPeriod && (
                  <motion.div
                    key="s3"
                    initial={{
                      opacity: 0,
                      x: 20,
                    }}
                    animate={{
                      opacity: 1,
                      x: 0,
                    }}
                    exit={{
                      opacity: 0,
                      x: -20,
                    }}
                    transition={{
                      duration: 0.35,
                    }}
                  >
                    <h2
                      style={{
                        fontFamily:
                          "'Playfair Display', serif",
                        fontSize:
                          "clamp(22px,2.5vw,26px)",
                        fontWeight: 800,
                        color: T.text,
                        marginBottom: 4,
                      }}
                    >
                      Confirm Registration
                    </h2>

                    <p
                      style={{
                        color:
                          T.textMuted,
                        marginBottom:
                          "clamp(24px,3vw,32px)",
                        fontSize:
                          "clamp(13px,1.1vw,14px)",
                      }}
                    >
                      Review your details
                      before finalising
                    </p>

                    {/* Personal Details */}

                    <div
                      className="t-confirm-section"
                      style={{
                        background:
                          T.offWhite,
                        borderRadius: 14,
                        padding:
                          "clamp(16px,2.5vw,24px)",
                        marginBottom: 16,
                        border:
                          `1px solid ${T.border}`,
                      }}
                    >
                      <SectionLabel
                        label="Personal Details"
                      />

                      <div
                        className="t-confirm-grid"
                        style={{
                          display:
                            "grid",
                          gridTemplateColumns:
                            "1fr 1fr",
                          gap:
                            "clamp(10px,1.5vw,16px)",
                        }}
                      >
                        {[
                          [
                            "Full Name",
                            `${formData.firstName} ${formData.lastName}`,
                          ],

                          [
                            "Email",
                            formData.email,
                          ],

                          [
                            "Phone",
                            formData.phone,
                          ],

                          formData.company && [
                            "Company",
                            formData.company,
                          ],
                        ]
                          .filter(Boolean)
                          .map(
                            ([
                              lbl,
                              val,
                            ]) => (
                              <div
                                key={lbl}
                              >
                                <span
                                  style={{
                                    fontSize:
                                      "clamp(10px,0.8vw,11px)",
                                    color:
                                      T.textMuted,
                                    textTransform:
                                      "uppercase",
                                    letterSpacing:
                                      "0.06em",
                                  }}
                                >
                                  {lbl}
                                </span>

                                <p
                                  style={{
                                    fontSize:
                                      "clamp(13px,1.1vw,14px)",
                                    fontWeight: 600,
                                    color:
                                      T.text,
                                    marginTop: 3,
                                  }}
                                >
                                  {val}
                                </p>
                              </div>
                            )
                          )}
                      </div>
                    </div>

                    {/* Event */}

                    {conferenceName && (
                      <div
                        style={{
                          background:
                            T.softTeal,
                          borderRadius: 10,
                          padding:
                            "clamp(12px,1.5vw,16px)",
                          marginBottom: 16,
                          border:
                            `1px solid ${T.border}`,
                        }}
                      >
                        <div
                          style={{
                            fontSize:
                              "clamp(10px,0.8vw,12px)",
                            color:
                              T.textMuted,
                            fontWeight: 600,
                            letterSpacing:
                              "0.05em",
                            textTransform:
                              "uppercase",
                          }}
                        >
                          Event
                        </div>

                        <div
                          style={{
                            fontSize:
                              "clamp(14px,1.2vw,16px)",
                            fontWeight: 700,
                            color:
                              T.text,
                          }}
                        >
                          {conferenceName}
                        </div>
                      </div>
                    )}

                    {/* Order Summary */}

                    <div
                      className="t-order-summary"
                      style={{
                        background:
                          `${T.primary}08`,
                        borderRadius:
                          "clamp(16px,2vw,20px)",
                        padding:
                          "clamp(20px,3vw,28px)",
                        marginBottom: 16,
                        border:
                          `1px solid ${T.primary}20`,
                      }}
                    >
                      <SectionLabel
                        label="Order Summary"
                      />

                      {/* Pass */}

                      <div
                        style={{
                          display:
                            "flex",
                          justifyContent:
                            "space-between",
                          alignItems:
                            "center",
                          marginBottom: 12,
                          flexWrap:
                            "wrap",
                          gap: 8,
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize:
                                "clamp(10px,0.8vw,11px)",
                              color:
                                T.primary,
                              fontWeight: 700,
                              letterSpacing:
                                "0.07em",
                              textTransform:
                                "uppercase",
                              marginBottom: 3,
                            }}
                          >
                            Pass
                          </div>

                          <div
                            style={{
                              fontSize:
                                "clamp(15px,1.4vw,17px)",
                              fontWeight: 800,
                              fontFamily:
                                "'Playfair Display', serif",
                              color:
                                T.text,
                            }}
                          >
                            {
                              selectedCategory.name
                            }
                          </div>

                          <div
                            style={{
                              fontSize:
                                "clamp(12px,1vw,13px)",
                              color:
                                T.textMuted,
                            }}
                          >
                            {
                              selectedPeriod.label
                            }
                          </div>
                        </div>

                        <div
                          style={{
                            fontSize:
                              "clamp(18px,2vw,22px)",
                            fontWeight: 800,
                            color:
                              T.primary,
                          }}
                        >
                          $
                          {passPrice}
                        </div>
                      </div>

                      {/* Accommodation */}

                      {selectedAccom &&
                        selectedAccom.value !==
                          "none" && (
                          <div
                            style={{
                              display:
                                "flex",
                              justifyContent:
                                "space-between",
                              alignItems:
                                "center",
                              paddingTop: 12,
                              borderTop:
                                `1px solid ${T.border}`,
                              marginBottom: 12,
                              flexWrap:
                                "wrap",
                              gap: 8,
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  fontSize:
                                    "clamp(10px,0.8vw,11px)",
                                  color:
                                    T.textMuted,
                                  fontWeight: 700,
                                  letterSpacing:
                                    "0.07em",
                                  textTransform:
                                    "uppercase",
                                  marginBottom: 3,
                                }}
                              >
                                Accommodation
                              </div>

                              <div
                                style={{
                                  fontSize:
                                    "clamp(13px,1.1vw,15px)",
                                  fontWeight: 600,
                                  color:
                                    T.text,
                                }}
                              >
                                {
                                  selectedAccom.label
                                }{" "}
                                —{" "}
                                {
                                  formData.nights
                                }{" "}
                                {formData.nights ===
                                1
                                  ? "Night"
                                  : "Nights"}
                              </div>
                            </div>

                            <div
                              style={{
                                fontSize:
                                  "clamp(16px,1.8vw,20px)",
                                fontWeight: 800,
                                color:
                                  T.accent,
                              }}
                            >
                              $
                              {(
                                selectedAccom.price *
                                formData.nights
                              ).toFixed(
                                2
                              )}
                            </div>
                          </div>
                        )}

                      {/* Subtotal + Processing Fee + Total */}

                      <div
                        style={{
                          borderTop:
                            `1px solid ${T.border}`,
                          paddingTop: 12,
                        }}
                      >
                        {/* Subtotal */}

                        <div
                          style={{
                            display:
                              "flex",
                            justifyContent:
                              "space-between",
                            marginBottom: 6,
                            flexWrap:
                              "wrap",
                            gap: 4,
                          }}
                        >
                          <span
                            style={{
                              fontSize:
                                "clamp(12px,1vw,13px)",
                              color:
                                T.textMuted,
                            }}
                          >
                            Subtotal
                          </span>

                          <span
                            style={{
                              fontSize:
                                "clamp(13px,1.1vw,15px)",
                              fontWeight: 600,
                              color:
                                T.text,
                            }}
                          >
                            $
                            {subtotal.toFixed(
                              2
                            )}
                          </span>
                        </div>

                        {/* 5% PROCESSING FEE */}

                        <div
                          style={{
                            display:
                              "flex",
                            justifyContent:
                              "space-between",
                            marginBottom: 6,
                            flexWrap:
                              "wrap",
                            gap: 4,
                          }}
                        >
                          <span
                            style={{
                              fontSize:
                                "clamp(12px,1vw,13px)",
                              color:
                                T.textMuted,
                            }}
                          >
                            Processing Fee
                            (5%)
                          </span>

                          <span
                            style={{
                              fontSize:
                                "clamp(13px,1.1vw,15px)",
                              fontWeight: 600,
                              color:
                                T.textMuted,
                            }}
                          >
                            $
                            {processingFee.toFixed(
                              2
                            )}
                          </span>
                        </div>

                        {/* Total */}

                        <div
                          style={{
                            display:
                              "flex",
                            justifyContent:
                              "space-between",
                            paddingTop: 12,
                            borderTop:
                              `2px solid ${T.border}`,
                            marginTop: 6,
                          }}
                        >
                          <span
                            style={{
                              fontSize:
                                "clamp(14px,1.3vw,16px)",
                              fontWeight: 700,
                              color:
                                T.text,
                            }}
                          >
                            Total
                          </span>

                          <span
                            className="t-total-amount"
                            style={{
                              fontSize:
                                "clamp(22px,2.8vw,28px)",
                              fontWeight: 900,
                              fontFamily:
                                "'Playfair Display', serif",
                              color:
                                T.accentGold,
                            }}
                          >
                            $
                            {totalAmount.toFixed(
                              2
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Special Requirements */}

                    {formData.specialRequirements && (
                      <div
                        style={{
                          background:
                            T.offWhite,
                          borderRadius: 12,
                          padding:
                            "clamp(14px,1.5vw,18px)",
                          marginBottom: 16,
                          border:
                            `1px solid ${T.border}`,
                        }}
                      >
                        <span
                          style={{
                            fontSize:
                              "clamp(10px,0.8vw,11px)",
                            color:
                              T.textMuted,
                            textTransform:
                              "uppercase",
                            letterSpacing:
                              "0.06em",
                          }}
                        >
                          Special Requirements
                        </span>

                        <p
                          style={{
                            fontSize:
                              "clamp(13px,1.1vw,14px)",
                            color:
                              T.text,
                            marginTop: 4,
                            lineHeight: 1.6,
                          }}
                        >
                          {
                            formData.specialRequirements
                          }
                        </p>
                      </div>
                    )}

                    {/* Terms */}

                    <label
                      style={{
                        display:
                          "flex",
                        alignItems:
                          "flex-start",
                        gap:
                          "clamp(8px,1vw,12px)",
                        cursor:
                          "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        name="agreeToTerms"
                        checked={
                          formData.agreeToTerms
                        }
                        onChange={
                          handleChange
                        }
                        style={{
                          width:
                            "clamp(16px,1.5vw,18px)",
                          height:
                            "clamp(16px,1.5vw,18px)",
                          marginTop: 2,
                          cursor:
                            "pointer",
                          accentColor:
                            T.primary,
                          flexShrink: 0,
                        }}
                      />

                      <span
                        style={{
                          fontSize:
                            "clamp(12px,1.1vw,14px)",
                          color:
                            T.textMuted,
                          lineHeight: 1.5,
                        }}
                      >
                        I agree to the{" "}
                        <Link
                          to="/terms"
                          style={{
                            color:
                              T.primary,
                            textDecoration:
                              "none",
                          }}
                        >
                          Terms &
                          Conditions
                        </Link>{" "}
                        and{" "}
                        <Link
                          to="/privacy-policy"
                          style={{
                            color:
                              T.primary,
                            textDecoration:
                              "none",
                          }}
                        >
                          Privacy Policy
                        </Link>
                      </span>
                    </label>
                  </motion.div>
                )}

              {/* ── STEP 4: SUCCESS ── */}

              {step === 4 && (
                <motion.div
                  key="s4"
                  initial={{
                    opacity: 0,
                    scale: 0.95,
                  }}
                  animate={{
                    opacity: 1,
                    scale: 1,
                  }}
                  transition={{
                    duration: 0.5,
                  }}
                  style={{
                    textAlign:
                      "center",
                    padding:
                      "clamp(24px,4vw,40px) clamp(12px,2vw,20px)",
                  }}
                >
                  <div
                    className="t-success-icon"
                    style={{
                      width:
                        "clamp(60px,8vw,80px)",
                      height:
                        "clamp(60px,8vw,80px)",
                      borderRadius:
                        "50%",
                      background:
                        `${T.success}10`,
                      border:
                        `1px solid ${T.success}30`,
                      display:
                        "flex",
                      alignItems:
                        "center",
                      justifyContent:
                        "center",
                      margin:
                        "0 auto 20px",
                    }}
                  >
                    <RiCheckLine
                      size="clamp(32px,4vw,44px)"
                      color={
                        T.success
                      }
                    />
                  </div>

                  <h2
                    className="t-success-title"
                    style={{
                      fontFamily:
                        "'Playfair Display', serif",
                      fontSize:
                        "clamp(24px,3vw,30px)",
                      fontWeight: 800,
                      color: T.text,
                      marginBottom: 8,
                    }}
                  >
                    Registration
                    Successful! 🎉
                  </h2>

                  <p
                    style={{
                      color:
                        T.textMuted,
                      marginBottom: 4,
                      fontSize:
                        "clamp(14px,1.2vw,16px)",
                    }}
                  >
                    Welcome,{" "}
                    {
                      formData.firstName
                    }{" "}
                    {
                      formData.lastName
                    }
                    !
                  </p>

                  {conferenceName && (
                    <p
                      style={{
                        color:
                          T.primary,
                        marginBottom: 4,
                        fontWeight: 600,
                        fontSize:
                          "clamp(13px,1.1vw,14px)",
                      }}
                    >
                      You are registered
                      for:{" "}
                      {
                        conferenceName
                      }
                    </p>
                  )}

                  <p
                    style={{
                      color:
                        T.textMuted,
                      marginBottom:
                        "clamp(24px,3vw,32px)",
                      fontSize:
                        "clamp(13px,1.1vw,14px)",
                    }}
                  >
                    A confirmation
                    email has been sent
                    to{" "}
                    <span
                      style={{
                        color:
                          T.primary,
                        fontWeight: 600,
                      }}
                    >
                      {
                        formData.email
                      }
                    </span>
                  </p>

                  <div
                    style={{
                      display:
                        "flex",
                      gap:
                        "clamp(8px,1vw,12px)",
                      justifyContent:
                        "center",
                      flexWrap:
                        "wrap",
                    }}
                  >
                    <Link
                      to="/"
                      className="t-btn-mobile"
                      style={{
                        display:
                          "inline-flex",
                        alignItems:
                          "center",
                        gap: 6,
                        background:
                          T.gradGold,
                        color:
                          T.navyDeep,
                        padding:
                          "clamp(10px,1.5vw,13px) clamp(24px,3vw,32px)",
                        borderRadius:
                          50,
                        fontSize:
                          "clamp(13px,1.1vw,14px)",
                        fontWeight: 700,
                        textDecoration:
                          "none",
                        transition:
                          "all 0.2s",
                        boxShadow:
                          T.shadowGold,
                      }}
                      onMouseEnter={(
                        e
                      ) => {
                        e.currentTarget.style.transform =
                          "translateY(-2px)";

                        e.currentTarget.style.boxShadow =
                          T.shadowLg;
                      }}
                      onMouseLeave={(
                        e
                      ) => {
                        e.currentTarget.style.transform =
                          "translateY(0)";

                        e.currentTarget.style.boxShadow =
                          T.shadowGold;
                      }}
                    >
                      Back to Home

                      <RiArrowRightLine
                        size="clamp(14px,1.2vw,16px)"
                      />
                    </Link>

                    {formData.eventId && (
                      <Link
                        to={`/event/${
                          slug ||
                          formData.eventId
                        }`}
                        className="t-btn-mobile"
                        style={{
                          display:
                            "inline-flex",
                          alignItems:
                            "center",
                          gap: 6,
                          background:
                            "transparent",
                          border:
                            `2px solid ${T.primary}`,
                          color:
                            T.primary,
                          padding:
                            "clamp(10px,1.5vw,13px) clamp(24px,3vw,32px)",
                          borderRadius:
                            50,
                          fontSize:
                            "clamp(13px,1.1vw,14px)",
                          fontWeight: 700,
                          textDecoration:
                            "none",
                          transition:
                            "all 0.2s",
                        }}
                        onMouseEnter={(
                          e
                        ) => {
                          e.currentTarget.style.background =
                            T.primary;

                          e.currentTarget.style.color =
                            T.white;
                        }}
                        onMouseLeave={(
                          e
                        ) => {
                          e.currentTarget.style.background =
                            "transparent";

                          e.currentTarget.style.color =
                            T.primary;
                        }}
                      >
                        View Event

                        <RiCalendarEventLine
                          size="clamp(14px,1.2vw,16px)"
                        />
                      </Link>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* ── NAV BUTTONS ── */}

            {step < 4 && (
              <div
                className="t-nav-buttons"
                style={{
                  display: "flex",
                  justifyContent:
                    "space-between",
                  marginTop:
                    "clamp(28px,4vw,40px)",
                  paddingTop:
                    "clamp(16px,2.5vw,24px)",
                  borderTop:
                    `1px solid ${T.border}`,
                  flexDirection:
                    "row",
                  gap:
                    "clamp(8px,1.5vw,12px)",
                }}
              >
                {step > 1 ? (
                  <button
                    type="button"
                    onClick={() =>
                      setStep(
                        (p) => p - 1
                      )
                    }
                    style={{
                      padding:
                        "clamp(10px,1.2vw,12px) clamp(20px,2.5vw,28px)",
                      background:
                        "transparent",
                      border:
                        `1px solid ${T.border}`,
                      borderRadius:
                        40,
                      fontSize:
                        "clamp(13px,1.1vw,14px)",
                      fontWeight: 600,
                      color:
                        T.textMuted,
                      cursor:
                        "pointer",
                      transition:
                        "all 0.2s",
                    }}
                    onMouseEnter={(
                      e
                    ) => {
                      e.currentTarget.style.borderColor =
                        T.primary;

                      e.currentTarget.style.color =
                        T.primary;
                    }}
                    onMouseLeave={(
                      e
                    ) => {
                      e.currentTarget.style.borderColor =
                        T.border;

                      e.currentTarget.style.color =
                        T.textMuted;
                    }}
                  >
                    Previous
                  </button>
                ) : (
                  <div />
                )}

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    padding:
                      "clamp(10px,1.2vw,12px) clamp(24px,3vw,36px)",
                    background:
                      T.gradGold,
                    border: "none",
                    borderRadius: 40,
                    fontSize:
                      "clamp(13px,1.1vw,14px)",
                    fontWeight: 700,
                    color:
                      T.navyDeep,
                    cursor:
                      loading
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      loading
                        ? 0.7
                        : 1,
                    transition:
                      "all 0.2s",
                    display:
                      "flex",
                    alignItems:
                      "center",
                    gap:
                      "clamp(6px,0.8vw,8px)",
                    boxShadow:
                      T.shadowGold,
                  }}
                  onMouseEnter={(
                    e
                  ) => {
                    if (!loading) {
                      e.currentTarget.style.transform =
                        "translateY(-2px)";

                      e.currentTarget.style.boxShadow =
                        T.shadowLg;
                    }
                  }}
                  onMouseLeave={(
                    e
                  ) => {
                    e.currentTarget.style.transform =
                      "translateY(0)";

                    e.currentTarget.style.boxShadow =
                      T.shadowGold;
                  }}
                >
                  {loading ? (
                    <>
                      <div
                        style={{
                          width:
                            "clamp(14px,1.2vw,16px)",
                          height:
                            "clamp(14px,1.2vw,16px)",
                          border:
                            "2px solid #1A3A4A",
                          borderTopColor:
                            "transparent",
                          borderRadius:
                            "50%",
                          animation:
                            "spin 0.8s linear infinite",
                        }}
                      />

                      Processing…
                    </>
                  ) : (
                    <>
                      {step === 3
                        ? "Complete Registration"
                        : "Continue"}

                      <RiArrowRightLine
                        size="clamp(14px,1.2vw,16px)"
                      />
                    </>
                  )}
                </button>
              </div>
            )}
          </form>
        </div>

        {/* Explore More About Summits Button */}

        <div
          style={{
            display: "flex",
            justifyContent:
              "center",
            marginTop:
              "clamp(32px,4vw,48px)",
          }}
        >
          <Link
            to="/events"
            className="t-btn-mobile"
            style={{
              display:
                "inline-flex",
              alignItems:
                "center",
              gap:
                "clamp(8px,1vw,12px)",
              background:
                "transparent",
              border:
                `2px solid ${T.primary}`,
              color:
                T.primary,
              padding:
                "clamp(10px,1.5vw,14px) clamp(20px,2.5vw,32px)",
              borderRadius: 50,
              fontSize:
                "clamp(13px,1.2vw,15px)",
              fontWeight: 700,
              textDecoration:
                "none",
              transition:
                "all 0.3s",
              cursor:
                "pointer",
            }}
            onMouseEnter={(
              e
            ) => {
              e.currentTarget.style.background =
                T.gradGold;

              e.currentTarget.style.color =
                T.navyDeep;

              e.currentTarget.style.transform =
                "translateY(-2px)";

              e.currentTarget.style.boxShadow =
                T.shadowGold;

              e.currentTarget.style.borderColor =
                "transparent";
            }}
            onMouseLeave={(
              e
            ) => {
              e.currentTarget.style.background =
                "transparent";

              e.currentTarget.style.color =
                T.primary;

              e.currentTarget.style.transform =
                "translateY(0)";

              e.currentTarget.style.boxShadow =
                "none";

              e.currentTarget.style.borderColor =
                T.primary;
            }}
          >
            <RiCalendarEventLine
              size="clamp(16px,1.5vw,20px)"
            />

            Explore More About
            Summits

            <RiArrowRightLine
              size="clamp(14px,1.2vw,16px)"
            />
          </Link>
        </div>
      </div>
    </div>
  );
}